import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";
import { Eye, FileText, CheckCircle2, XCircle, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Textarea } from "@shared/ui/textarea";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";

interface QuickActionsProps {
  operation?: any;
  onRefetch?: () => void;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ operation, onRefetch }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const statusStr = (operation?.statusDisplay || operation?.status_display || operation?.statusName || operation?.status_name || '').toLowerCase();
  const statusVal = Number(operation?.status ?? operation?.status_code ?? 0);

  const isCompleted =
    statusVal === 2 ||
    statusVal === 4;

  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);

  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");

  const resourceId =
    operation?.resourceCard?.id ||
    operation?.resourceOverview?.id ||
    operation?.resource ||
    operation?.resourceCard?.resourceId;

  const handleViewResource = () => {
    if (resourceId) {
      navigate(`/mx/manage/resources/view/${resourceId}`);
    } else {
      navigate(`/mx/manage/resources`);
    }
  };

  const handleAddNoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteContent.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter note content.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Note Added",
      description: "Note added successfully to the operation.",
    });
    setNoteTitle("");
    setNoteContent("");
    setIsAddNoteOpen(false);
    onRefetch?.();
  };

  const handleMarkCompleted = () => {
    toast({
      title: "Operation Completed",
      description: "Operation has been marked as completed successfully.",
    });
    setIsCompleteOpen(false);
    onRefetch?.();
  };

  const handleReject = () => {
    toast({
      title: "Operation Rejected",
      description: "Operation has been rejected successfully.",
      variant: "destructive",
    });
    setIsRejectOpen(false);
    onRefetch?.();
  };

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm flex flex-col space-y-4">
      <h3 className="text-base font-bold text-gray-900">Quick Actions</h3>

      <div className="flex flex-col gap-3">
        {/* 1. View Resource */}
        <Button
          variant="ghost"
          onClick={handleViewResource}
          className="w-full justify-start border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-800 font-medium rounded-lg px-4 py-2.5 text-sm"
        >
          <Eye className="h-4 w-4 mr-2.5 text-blue-600 shrink-0" />
          View Resource
        </Button>

        {/* 2. Add Note */}
        <Button
          variant="ghost"
          disabled={isCompleted}
          onClick={() => setIsAddNoteOpen(true)}
          className="w-full justify-start border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-800 font-medium rounded-lg px-4 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FileText className="h-4 w-4 mr-2.5 text-purple-600 shrink-0" />
          Add Note
        </Button>

        {/* 3. Mark Completed */}
        <Button
          variant="ghost"
          disabled={isCompleted}
          onClick={() => setIsCompleteOpen(true)}
          className="w-full justify-start border border-emerald-200 bg-emerald-50/70 hover:bg-emerald-100 text-emerald-800 font-medium rounded-lg px-4 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle2 className="h-4 w-4 mr-2.5 text-emerald-600 shrink-0" />
          Mark Completed
        </Button>

        {/* 4. Reject */}
        <Button
          variant="ghost"
          disabled={isCompleted}
          onClick={() => setIsRejectOpen(true)}
          className="w-full justify-start border border-rose-200 bg-rose-50/70 hover:bg-rose-100 text-rose-700 font-medium rounded-lg px-4 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircle className="h-4 w-4 mr-2.5 text-rose-600 shrink-0" />
          Reject
        </Button>
      </div>

      {/* Add Note Dialog */}
      <Dialog open={isAddNoteOpen} onOpenChange={setIsAddNoteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a note or instruction for this operation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddNoteSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="noteTitle">Note Title (Optional)</Label>
              <Input
                id="noteTitle"
                placeholder="e.g. Field Inspection Remark"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="noteContent">Message / Details</Label>
              <Textarea
                id="noteContent"
                placeholder="Enter note details here..."
                rows={4}
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddNoteOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5">
                <Send className="h-4 w-4" />
                Add Note
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Completed Dialog */}
      <Dialog open={isCompleteOpen} onOpenChange={setIsCompleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Operation Completed</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this operation as completed?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCompleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleMarkCompleted}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Confirm Complete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Operation</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject this operation?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsRejectOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleReject}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Confirm Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickActions;
